import React from "react";
import {
    useGetSessionSetUpProgramManagersByEventKeyQuery,
    usePutProgramManagerSessionMutation,
} from "../../../../../services/cclTokenedSessionApi";
import CclGenericAccordian from "../../../../../components/common/cclGenericAccordian";
import SessionProgramManagersTable from "./sessionProgramManagersTable";
import AddEditProgramManagerDrawer from "./addEditProgramManagerDrawer";
import { SessionProgramManager } from "../../../../../services/types/sessionDetailTypes";
import { Alert, AlertColor, Stack } from "@mui/material";
import useLogAccessEvent from "../../../../../hooks/useLogAccessEvent";
import { AccessEventProgramManagerDetails } from "../../../../../services/types/accessEventTypes";

export interface SessionProgramManagersAccordionProps {
    esKey: number;
    programCode: string;
    hasAssessmentPlatform: boolean;
    hasVenue: boolean;
    showSnackbar: (message: string, severity: AlertColor) => void;
}

export const SessionProgramManagersAccordion: React.FC<SessionProgramManagersAccordionProps> = (
    props,
) => {
    const [selectedPM, setSelectedPM] = React.useState<SessionProgramManager | null>(null);
    const [openAddEditDrawer, setOpenAddEditDrawer] = React.useState<boolean>(false);
    const {
        data: programManagers,
        isSuccess,
        isLoading,
        isError,
    } = useGetSessionSetUpProgramManagersByEventKeyQuery(props.esKey.toString(), {
        skip: !props.esKey,
    });
    const [saveProgramManager, { isLoading: removing }] = usePutProgramManagerSessionMutation();
    const { logEvent } = useLogAccessEvent();

    const removeProgramManager = (id: number) => {
        var removeManager = programManagers?.find((pm) => pm.individualId === id);
        if (!removeManager) {
            props.showSnackbar("Client Administrator Removal Failed", "error");
            return;
        }
        var payload: SessionProgramManager = { ...removeManager };
        payload.programId = props.esKey;
        payload.status = "Remove";
        saveProgramManager(payload)
            .unwrap()
            .then(() => {
                const evtData: AccessEventProgramManagerDetails = {
                    sessionID: props.esKey.toString(),
                    projectCode: props.programCode ?? "",
                    clientAdminId: removeManager?.individualId.toString() ?? "",
                    clientAdminEmail: removeManager?.email ?? "",
                };
                logEvent("ClientAdminRemoved", evtData);
                props.showSnackbar("Client Administrator Removed", "success");
            })
            .catch(() => {
                props.showSnackbar("Client Administrator Removal Failed", "error");
            });
    };

    const handleEvent = (evt: string, id: number) => {
        switch (evt) {
            case "add":
                setSelectedPM(null);
                setOpenAddEditDrawer(true);
                break;

            case "remove":
                removeProgramManager(id);
                break;
        }
    };

    return (
        <CclGenericAccordian headingText="Client Administrators" sx={{ h: 1 }}>
            <Stack width={1} spacing={2} padding={3}>
                {openAddEditDrawer ? (
                    <AddEditProgramManagerDrawer
                        open
                        esKey={props.esKey}
                        programCode={props.programCode}
                        onCancel={() => setOpenAddEditDrawer(false)}
                        managerToEdit={selectedPM}
                        showSnackbar={props.showSnackbar}
                    />
                ) : null}
                {!props.hasAssessmentPlatform || !props.hasVenue ? (
                    <Alert severity="info" sx={{ width: "100%" }}>
                        Venue must be specified and Assessment Platform set to Assessment Portal in
                        D365 in order to access this feature.
                    </Alert>
                ) : isSuccess || isLoading ? (
                    <SessionProgramManagersTable
                        ProgramManagersList={programManagers}
                        isLoading={isLoading}
                        handleEvent={handleEvent}
                        removing={removing}
                    />
                ) : isError ? (
                    <Alert severity="error" sx={{ width: "100%" }}>
                        There was an error retrieving Client Administrators for this session.
                    </Alert>
                ) : null}
            </Stack>
        </CclGenericAccordian>
    );
};
export default SessionProgramManagersAccordion;
